import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import LiberarAulas from "../componentes/LiberarAulas";
import Carregando from "../componentes/Carregando";
import Rodape from "../componentes/Rodape";
import ModalVigencias from "../componentes/ModalVigencias";
import { MeuLivroPorConta } from "./MeuLivro";
import {
  formatarData,
  formatarMesAno,
  primeiroNome,
  saudacao,
  useHub,
  vigenciaMaisProxima,
  type CursoDoAluno,
} from "../lib/dados";
import { Aviso, ChipTrancado, EscudoFBC, SeloVigencia, SetaDireita, Topo } from "../componentes/ui";
import { AREAS, SLUGS_COBERTOS, destinoDaArea, type Area } from "../lib/areas";

// A tela inicial. Mostra pouco de propósito: a saudação, três fatos da conta e
// as portas dos cursos. A densidade — ferramentas, secretaria, notas — mora um
// nível abaixo, porque **ferramenta aparece dentro do seu curso**, nunca solta
// aqui. A versão anterior desta tela listava os 31 cards de todos os cursos
// juntos, e era exatamente o que o dono do produto não queria.
//
// A exceção são os cards de escopo 'conta' (000038): o Contrate um Contador não
// é de curso nenhum — ninguém o compra, e ele aparecia repetido em cada curso do
// aluno como se pertencesse a todos. Ele teve seção própria aqui até 29/10; agora
// tem página própria (`/contrate-um-contador`), aberta pela área dele, como no Bubble.
//
// Os três fatos usam só dado que o hub tem hoje. Nada de "continue de onde
// parou", streak ou heatmap: progresso de aula mora no Memberkit, e widget
// alimentado por dado inexistente destrói a credibilidade dos que têm dado real.
export default function Inicio() {
  const estado = useHub();
  // Depois que o aluno confirma a senha, o banner some sem recarregar a página —
  // `mk_senha_em` deixou de ser nulo no banco, mas o hub já está em memória.
  const [senhaLiberada, setSenhaLiberada] = useState(false);
  const [vendoVigencias, setVendoVigencias] = useState(false);

  if (estado.fase === "carregando") return <Carregando />;

  if (estado.fase === "erro") {
    return (
      <Aviso titulo="Algo deu errado">
        Não conseguimos carregar a sua área agora. Recarregue a página; se continuar assim, fale com o
        suporte que a gente resolve.
      </Aviso>
    );
  }

  if (estado.fase === "sem-vinculo") {
    return (
      <Aviso
        titulo="Não encontramos o seu cadastro"
        acao={
          <button onClick={() => void supabase.auth.signOut()} className="btn-contorno">
            Sair e tentar com outro e-mail
          </button>
        }
      >
        A sua conta foi criada, mas nenhuma compra está ligada a este e-mail. Se você comprou com{" "}
        <strong className="text-tinta">outro e-mail</strong>, saia e refaça o primeiro acesso usando o
        e-mail da compra. Se o problema persistir, fale com o suporte.
      </Aviso>
    );
  }

  const { hub } = estado;
  const todos = [...hub.cursos, ...hub.faculdade];

  // Perfil vinculado, zero acesso VIGENTE. Antes de dizer "prazo vencido", uma
  // pergunta: esta conta é de um CLIENTE MEI (000078)? O cliente não é aluno e
  // nunca teve curso — para ele a tela certa é o livro-caixa dele, e a mensagem
  // de prazo seria um erro sobre uma compra que nunca existiu. Para o aluno de
  // verdade com prazo vencido, o aviso continua o mesmo.
  if (todos.length === 0) {
    return (
      <MeuLivroPorConta
        quandoNaoCliente={
          <div className="min-h-screen">
            <Topo atual="inicio" nome={hub.nome} avatarUrl={hub.avatarUrl} />
            {/* O acervo é oferecido AQUI porque este é o beco: quem chega a
                esta tela é exatamente quem não tem mais curso, e até a 000281
                ela não levava a lugar nenhum. O link é sempre oferecido, e não
                só quando há documento: perguntar ao banco antes exigiria uma
                chamada a mais para, no pior caso, esconder do formado o único
                lugar onde o certificado dele mora. A tela de destino diz com
                todas as letras quando está vazia. */}
            <Aviso
              titulo="Nenhum curso ativo neste momento"
              acao={
                <Link to="/meus-documentos" className="pill-cta">
                  Ver os meus documentos
                </Link>
              }
            >
              Encontramos o seu cadastro, mas nenhum curso está vigente agora — o prazo de acesso
              pode ter chegado ao fim. Se você concluiu um curso, o seu certificado continua
              disponível para download. Se acha que isso está errado, fale com o suporte que a
              gente confere.
            </Aviso>
          </div>
        }
      />
    );
  }

  const proxima = vigenciaMaisProxima(todos);
  const nome = primeiroNome(hub.nome);
  const pedindoSenha = hub.senhaPendenteNasAulas && !senhaLiberada;
  const cursosTrancados = todos.filter((c) => c.trancado);
  const temCursoTrancado = cursosTrancados.length > 0;
  // Navegação por ÁREA (N11): as 6 áreas do Bubble, sempre todas. O acesso de cada
  // uma sai do que o aluno já tem; curso que não cai em nenhuma área não some —
  // aparece em "Outros cursos".
  const slugsComAcesso = new Set(todos.map((c) => c.slug));
  const temContrate = hub.cardsDeConta.length > 0;
  const cursosOrfaos = todos.filter((c) => !SLUGS_COBERTOS.has(c.slug));

  return (
    <div className="min-h-screen">
      <Topo atual="inicio" nome={hub.nome} avatarUrl={hub.avatarUrl} />

      <div className="painel-navy pt-10 pb-24 sm:pt-12">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h1 className="rise text-3xl font-bold text-white sm:text-4xl">
            {saudacao()}
            {nome ? `, ${nome}` : ""}!
          </h1>
          <p className="rise mt-2 text-navy-mute" style={{ "--i": 1 } as React.CSSProperties}>
            {resumoDoAcesso(hub.cursos.length, hub.faculdade.length)}
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 pb-16 sm:px-6">
        {/* Pedido de senha para as aulas: fica acima de tudo porque é a única
            coisa nesta tela que só o aluno pode resolver, e enquanto não
            resolver a senha dele nas aulas é outra. */}
        {pedindoSenha && (
          <div className="relative z-10 -mt-14 mb-4">
            <LiberarAulas aoConcluir={() => setSenhaLiberada(true)} />
          </div>
        )}

        {/* Contrato pendente explicado JÁ NA PRIMEIRA TELA. A revisão de 14/08
            pegou o buraco: os cards mostravam o cadeado "Assine para liberar" e
            a explicação só existia um clique adiante, dentro do curso — o aluno
            via a conta inteira trancada sem uma frase dizendo por quê. */}
        {temCursoTrancado && (
          <div
            role="alert"
            className={`surface rise relative z-10 mb-4 flex flex-wrap items-start gap-4 border-laranja/50 p-5 ${
              pedindoSenha ? "" : "-mt-14"
            }`}
            style={{ "--i": 1 } as React.CSSProperties}
          >
            <span className="grid size-11 shrink-0 place-items-center rounded-[13px] bg-alerta-fundo text-alerta-tinta">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-[23px]" aria-hidden="true">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
                <path d="M8 17c1.5-2.5 3-3.5 4-2.5s2 2 4-.5" />
              </svg>
            </span>
            <div className="min-w-64 flex-1">
              <h2 className="font-display font-bold text-titulo">
                {cursosTrancados.length === todos.length
                  ? "Falta assinar o contrato dos seus cursos"
                  : `Falta assinar o contrato de ${cursosTrancados.length === 1 ? "1 curso" : `${cursosTrancados.length} cursos`}`}
              </h2>
              <p className="mt-1 text-sm leading-relaxed text-cinza">
                As aulas e as ferramentas liberam assim que a assinatura for concluída.{" "}
                <strong className="text-tinta">O seu acesso não foi perdido</strong> — o cadeado nos
                cards é só a assinatura que falta. Abra o curso para ver como resolver.
              </p>
            </div>
          </div>
        )}

        {/* Os cards de fatos assentam metade dentro, metade fora do painel azul —
            é o gesto assinatura da marca (painel azul + cards claros por cima).
            Quando algum banner está presente (senha ou contrato), é ele que faz
            esse encaixe, e os fatos descem. */}
        <div
          className={`relative z-10 grid gap-4 sm:grid-cols-3 ${
            pedindoSenha || temCursoTrancado ? "" : "-mt-14"
          }`}
        >
          <Fato
            i={2}
            rotulo="Cursos ativos"
            valor={<span className="tabular-nums">{todos.length}</span>}
            detalhe={detalheDosCursos(hub.cursos, hub.faculdade)}
          />
          {proxima ? (
            <Fato
              i={3}
              aoClicar={() => setVendoVigencias(true)}
              chamada="Ver todos os acessos"
              rotulo="Vigência mais próxima"
              valor={
                <span className="block text-lg leading-snug">
                  {/* Nome de pós ocupa três linhas e empurra a data para fora do
                      card; duas linhas e reticências mantêm a data à vista, que
                      é a informação do fato. */}
                  <span className="line-clamp-2" title={proxima.nome}>
                    {proxima.nome}
                  </span>
                  <span className="mt-0.5 block text-xl tabular-nums">
                    até {formatarData(proxima.fim_efetivo)}
                  </span>
                </span>
              }
              detalhe={detalheDaVigencia(todos, proxima)}
            />
          ) : (
            <Fato
              i={3}
              aoClicar={() => setVendoVigencias(true)}
              chamada="Ver todos os acessos"
              rotulo="Seu acesso"
              valor={
                <span>
                  Vitalício <span aria-hidden="true">∞</span>
                </span>
              }
              detalhe="Sem prazo para acabar. Nada para renovar."
            />
          )}
          <Fato
            i={4}
            rotulo="Ferramentas e recursos"
            valor={
              <span className="tabular-nums">
                {hub.cardsDeCurso.length + hub.cardsDeConta.length}
              </span>
            }
            detalhe="Incluídos nos seus cursos, sem custo extra."
          />
        </div>

        <section className="pt-12">
          <h2 className="text-2xl font-bold text-titulo">Áreas</h2>
          <p className="mt-1 text-sm text-cinza">
            Tudo o que o Contabilidade Facilitada tem, por área. As aulas e as ferramentas ficam
            dentro de cada uma.
          </p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {AREAS.map((area, i) => (
              <CardDeArea
                key={area.id}
                area={area}
                slugsComAcesso={slugsComAcesso}
                temContrate={temContrate}
                i={i + 1}
              />
            ))}
          </div>
        </section>

        {/* Curso do aluno que ainda não cai em nenhuma área (produto novo) não pode
            sumir da home — a cicatriz do card órfão. Aparece aqui até a área dele
            existir. Hoje todos os slugs do seed estão cobertos, então é defesa. */}
        {cursosOrfaos.length > 0 && (
          <section className="pt-12">
            <h2 className="text-2xl font-bold text-titulo">Outros cursos</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {cursosOrfaos.map((curso, i) => (
                <PortaDoCurso key={curso.slug} curso={curso} i={i + 1} />
              ))}
            </div>
          </section>
        )}

        {hub.faculdade.length > 0 ? (
          <FaixaFaculdade cursos={hub.faculdade} />
        ) : (
          <VitrineFaculdade />
        )}

        <ModalVigencias
          cursos={todos}
          aberto={vendoVigencias}
          aoFechar={() => setVendoVigencias(false)}
        />

        <Rodape />
      </div>
    </div>
  );
}

function resumoDoAcesso(cursos: number, faculdade: number): string {
  const partes: string[] = [];
  if (cursos > 0) partes.push(cursos === 1 ? "1 curso" : `${cursos} cursos`);
  if (faculdade > 0) {
    // "1 curso e 1 curso na faculdade" repetia a palavra e lia mal.
    partes.push(faculdade === 1 ? "1 curso na faculdade" : `${faculdade} cursos na faculdade`);
  }
  return `Você tem ${partes.join(" e ")} com acesso ativo.`;
}

/**
 * Nomes dos cursos, ou a divisão entre os dois mundos quando são muitos.
 *
 * Listar os nomes é melhor enquanto caberem: o aluno reconhece o que é dele. A
 * partir de quatro, o nome de pós é longo o bastante para a lista ser cortada no
 * meio de uma palavra ("MBA em…"), e aí a contagem informa mais que meia frase.
 */
function detalheDosCursos(cursos: CursoDoAluno[], faculdade: CursoDoAluno[]): string {
  const todos = [...cursos, ...faculdade];
  if (todos.length <= 3) return todos.map((c) => c.nome).join(" · ");
  const partes: string[] = [];
  if (cursos.length > 0) partes.push(cursos.length === 1 ? "1 curso livre" : `${cursos.length} cursos livres`);
  if (faculdade.length > 0) {
    partes.push(faculdade.length === 1 ? "1 na faculdade" : `${faculdade.length} na faculdade`);
  }
  return partes.join(" · ");
}

/**
 * O que mais o aluno precisa saber junto da data que vence primeiro. Sem esta
 * linha, quem tem um curso vitalício e outro vencendo lê a data como se o hub
 * inteiro fosse expirar.
 */
function detalheDaVigencia(todos: CursoDoAluno[], proxima: CursoDoAluno): string {
  const vitalicios = todos.filter((c) => c.vitalicio);
  if (vitalicios.length > 0) {
    return vitalicios.length === 1
      ? `${vitalicios[0].nome} é vitalício e não vence.`
      : `Outros ${vitalicios.length} cursos seus são vitalícios e não vencem.`;
  }
  const outros = todos.filter((c) => c.slug !== proxima.slug).length;
  if (outros === 0) return "Este é o seu único curso ativo.";
  return outros === 1
    ? "O seu outro curso vence depois desta data."
    : `Os seus outros ${outros} cursos vencem depois desta data.`;
}

function Fato({
  i,
  rotulo,
  valor,
  detalhe,
  aoClicar,
  chamada,
}: {
  i: number;
  rotulo: string;
  valor: React.ReactNode;
  detalhe: string;
  /** Quando passado, o fato inteiro vira botão — é o caso da vigência. */
  aoClicar?: () => void;
  chamada?: string;
}) {
  const conteudo = (
    <>
      <span className="text-sm font-semibold text-cinza">{rotulo}</span>
      <span className="mt-1 font-display text-2xl font-bold text-titulo">{valor}</span>
      <span className="mt-auto flex items-center gap-1.5 pt-2 text-xs leading-relaxed text-cinza">
        {chamada ? (
          <>
            <span className="font-semibold text-link underline underline-offset-3">{chamada}</span>
            <SetaDireita className="size-3.5 text-laranja-texto" />
          </>
        ) : (
          <span className="line-clamp-2" title={detalhe}>
            {detalhe}
          </span>
        )}
      </span>
    </>
  );

  const base = "surface rise flex min-h-32 flex-col p-5";
  const estilo = { "--i": i } as React.CSSProperties;

  if (!aoClicar) {
    return (
      <div className={base} style={estilo}>
        {conteudo}
      </div>
    );
  }
  return (
    <button type="button" onClick={aoClicar} className={`${base} porta text-left`} style={estilo}>
      {conteudo}
    </button>
  );
}

function PortaDoCurso({ curso, i }: { curso: CursoDoAluno; i: number }) {
  return (
    <Link
      to={`/curso/${curso.slug}`}
      className="surface porta rise flex min-h-36 flex-col gap-3 p-6"
      style={{ "--i": i } as React.CSSProperties}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-display text-xl font-bold text-titulo">{curso.nome}</h3>
        {/* Curso trancado mostra o motivo no lugar da vigência: a data não é a
            informação urgente quando o aluno não consegue entrar. */}
        {curso.trancado ? <ChipTrancado /> : <SeloVigencia vigencia={curso} />}
      </div>
      {/* Texto em azul, não no laranja da marca: #c76a00 sobre branco dá 3,87:1
          e reprova para texto de 16px. O laranja do CF é cor de FUNDO de CTA
          (com texto azul por cima) — como texto ele não passa. A seta pode ser
          laranja porque ícone é elemento gráfico, e aí 3:1 basta. */}
      <span className="mt-auto inline-flex items-center gap-2 font-bold text-link">
        Abrir curso
        <span className="text-laranja-texto">
          <SetaDireita />
        </span>
      </span>
    </Link>
  );
}

/**
 * Card de ÁREA da inicial (N11) — as 6 áreas do Bubble. Mostra-se sempre; quando
 * o aluno não tem acesso, vira um card "Sem acesso" sem link (o Bubble mostra as 6
 * áreas de qualquer jeito). Reaproveita o molde da `PortaDoCurso`.
 */
function CardDeArea({
  area,
  slugsComAcesso,
  temContrate,
  i,
}: {
  area: Area;
  slugsComAcesso: ReadonlySet<string>;
  temContrate: boolean;
  i: number;
}) {
  const { temAcesso, destino } = destinoDaArea(area, slugsComAcesso, temContrate);
  const base = "surface rise flex min-h-36 flex-col gap-3 p-6";
  const estilo = { "--i": i } as React.CSSProperties;

  const conteudo = (
    <>
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-display text-xl font-bold text-titulo">{area.nome}</h3>
        {!temAcesso && (
          <span className="inline-flex shrink-0 items-center rounded-full bg-chip px-2.5 py-0.5 text-xs font-semibold text-chip-tinta">
            Sem acesso
          </span>
        )}
      </div>
      <p className="text-sm leading-snug text-cinza">{area.descricao}</p>
      {temAcesso && destino && (
        <span className="mt-auto inline-flex items-center gap-2 font-bold text-link">
          Entrar
          <span className="text-laranja-texto">
            <SetaDireita />
          </span>
        </span>
      )}
    </>
  );

  if (!temAcesso || !destino) {
    return (
      <div className={base} style={estilo}>
        {conteudo}
      </div>
    );
  }
  return (
    <Link to={destino} className={`${base} porta`} style={estilo}>
      {conteudo}
    </Link>
  );
}

/**
 * A Faculdade na tela inicial: uma faixa navy separada dos cursos livres. A
 * separação visual É a separação que o dono pediu — a FBC tem matrícula, turma,
 * secretaria e notas, e não é "mais um curso" na mesma prateleira.
 */
function FaixaFaculdade({ cursos }: { cursos: CursoDoAluno[] }) {
  // Com oito pós a faixa passava a dominar a tela inicial, que é justamente a
  // tela que existe para mostrar pouco. Três cards e a contagem do resto — que
  // é dita em texto, não escondida — e o botão leva ao lugar onde estão todos.
  const MOSTRAR = 3;
  const visiveis = cursos.slice(0, MOSTRAR);
  const restantes = cursos.length - visiveis.length;

  return (
    <section className="painel-navy mt-12 rounded-[1.125rem] p-6 sm:p-8">
      <div className="flex items-center gap-4">
        <EscudoFBC className="size-11" />
        <div>
          <h2 className="text-2xl font-bold text-white">Minha Faculdade</h2>
          <p className="mt-0.5 text-xs uppercase tracking-[0.14em] text-navy-mute">
            Faculdade Brasileira de Contabilidade
          </p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-stretch gap-4">
        {visiveis.map((c) => (
          <div key={c.slug} className="tv-card min-w-72 flex-1 p-5">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <p className="font-display font-semibold text-white">{c.nome}</p>
              {/* O cadeado aparece aqui também: sem ele o card mostrava
                  "Matrícula ativa" e nada sobre a assinatura que falta, e o aluno
                  só descobria ao abrir a Faculdade. */}
              {c.trancado && <ChipTrancado sobreNavy />}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-navy-mute">
              {c.matricula?.turma && (
                <span>
                  Turma <b className="font-semibold tabular-nums text-navy-ink">{c.matricula.turma}</b>
                </span>
              )}
              {c.matricula?.status === "ativa" && (
                <span className="inline-flex items-center rounded-full bg-sucesso/15 px-2.5 py-0.5 text-xs font-semibold text-sucesso-claro">
                  Matrícula ativa
                </span>
              )}
              {c.matricula?.previsao_termino && (
                <span>
                  Término previsto:{" "}
                  <b className="font-semibold tabular-nums text-navy-ink">
                    {formatarMesAno(c.matricula.previsao_termino)}
                  </b>
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <Link to="/faculdade" className="pill-cta">
          Entrar na Faculdade <SetaDireita />
        </Link>
        {restantes > 0 && (
          <p className="text-sm text-navy-mute">
            {restantes === 1
              ? "e mais 1 curso na faculdade"
              : `e mais ${restantes} cursos na faculdade`}
          </p>
        )}
      </div>
    </section>
  );
}

/**
 * Para quem não tem curso na faculdade: a FBC aparece, mas como convite.
 *
 * Decisão de Victor em 12/08. A alternativa que estava no ar era esconder a
 * seção inteira, e esconder tem um custo: o aluno da Escola Contábil não fica
 * sabendo que a pós existe, e quem se matriculou e não vê o curso não tem onde
 * clicar para entender. O destino é `/faculdade`, que explica o que é a FBC e o
 * que fazer se a matrícula não apareceu — nenhum link de venda é inventado aqui.
 */
function VitrineFaculdade() {
  return (
    <section className="painel-navy mt-12 rounded-[1.125rem] p-6 sm:p-8">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div className="flex items-start gap-4">
          <EscudoFBC className="size-11" />
          <div className="max-w-xl">
            <h2 className="text-2xl font-bold text-white">Conheça a FBC</h2>
            <p className="mt-0.5 text-xs uppercase tracking-[0.14em] text-navy-mute">
              Faculdade Brasileira de Contabilidade
            </p>
            <p className="mt-3 text-navy-mute">
              É onde ficam o MBA, a Formação em Perícia e as pós-graduações do Contabilidade
              Facilitada — com matrícula, turma, secretaria e boletim. Se você já se matriculou e o
              curso não aparece aqui, veja como resolver.
            </p>
          </div>
        </div>
        <Link to="/faculdade" className="pill-cta">
          Ver a Faculdade <SetaDireita />
        </Link>
      </div>
    </section>
  );
}
